import { getApiGatewayUrl } from './apiGatewayUrl';
import type { TokenMetadata } from '../types/models/User';

/**
 * Single-flight session refresh.
 *
 * The access token lives in a short-lived (1h) HttpOnly cookie and is renewed
 * by calling POST /api/auth/refresh. Two mechanisms need to trigger a refresh:
 *
 *  1. `useTokenRefresh` — proactively, ~5 min before expiry.
 *  2. The axios / orval 401 handlers — reactively, the instant a request finds
 *     the cookie already expired (the gap the proactive timer can miss, e.g. a
 *     throttled background tab or a burst of concurrent requests).
 *
 * These can fire at the same moment (classic case: the user re-focuses a tab
 * whose proactive timer was throttled AND immediately clicks something). The
 * backend ROTATES the refresh token on every successful refresh — it revokes
 * the presented token and issues a new one (see TokenRefreshService.RefreshAsync).
 * So two concurrent refreshes would both present the same token; the first
 * rotates it, the second sees `IsRevoked` and gets a 401 — needlessly logging
 * out an otherwise-valid session. This module funnels ALL refresh callers
 * through one shared in-flight promise so exactly one /api/auth/refresh is ever
 * outstanding.
 *
 * Uses native `fetch` (NOT axiosInstance) on purpose: a refresh that itself
 * receives a 401 must not re-enter the axios response interceptor, or it would
 * recurse / double-dispatch the logout event.
 */

const CSRF_COOKIE_NAME = 'xsrf_token';
const CSRF_HEADER_NAME = 'X-CSRF-Token';
const REFRESH_ENDPOINT = '/api/auth/refresh';
const TOKEN_METADATA_KEY = 'token_metadata';

export interface SessionRefreshResult {
  /** True when the server issued a fresh access cookie. */
  ok: boolean;
  /**
   * HTTP status of the refresh call, when a response was received. Absent on a
   * network error. Callers use `status === 401` to tell a dead session
   * (log out) apart from a transient failure (keep session, maybe retry).
   */
  status?: number;
}

function getCookie(name: string): string | null {
  if (typeof document === 'undefined' || !document.cookie) {
    return null;
  }
  const cookie = document.cookie.split('; ').find((row) => row.startsWith(`${name}=`));
  if (!cookie) {
    return null;
  }
  const [, value] = cookie.split('=');
  return decodeURIComponent(value);
}

/**
 * Persist the new expiry so the proactive timer schedules the next refresh
 * correctly. Best-effort — a missing / non-JSON body must not fail a refresh
 * the server already honoured.
 */
function syncTokenMetadata(expiresAt: string): void {
  try {
    const raw = localStorage.getItem(TOKEN_METADATA_KEY);
    const meta = (raw ? JSON.parse(raw) : {}) as Partial<TokenMetadata>;
    localStorage.setItem(TOKEN_METADATA_KEY, JSON.stringify({ ...meta, expiresAt }));
  } catch {
    // Ignore parse / storage errors — the cookie is what actually authenticates.
  }
}

async function performRefresh(): Promise<SessionRefreshResult> {
  const baseUrl = getApiGatewayUrl();
  const csrfToken = getCookie(CSRF_COOKIE_NAME);

  let response: Response;
  try {
    response = await fetch(`${baseUrl}${REFRESH_ENDPOINT}`, {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        'X-Correlation-Id': crypto.randomUUID(),
        ...(csrfToken ? { [CSRF_HEADER_NAME]: csrfToken } : {}),
      },
    });
  } catch {
    // Network error — transient, no HTTP status to report.
    return { ok: false };
  }

  if (!response.ok) {
    return { ok: false, status: response.status };
  }

  try {
    const data = (await response.json()) as { expiresAt?: string };
    if (data?.expiresAt) {
      syncTokenMetadata(data.expiresAt);
    }
  } catch {
    // Body parsing is best-effort; the refresh still succeeded.
  }

  return { ok: true, status: response.status };
}

let inFlight: Promise<SessionRefreshResult> | null = null;

/**
 * Refresh the session, coalescing concurrent callers onto a single request.
 * Never rejects — always resolves to a {@link SessionRefreshResult}.
 */
export function refreshSession(): Promise<SessionRefreshResult> {
  if (inFlight) {
    return inFlight;
  }
  inFlight = performRefresh().finally(() => {
    inFlight = null;
  });
  return inFlight;
}
